import { useToast } from "@chakra-ui/react";
import dayjs from "@tether-web-portal/dayjs-setup";
import { DATE_FORMATS } from "@tether-web-portal/constants/dateFormats";
import { useCallback, useMemo, useState } from "react";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import { useTetherToast } from "@tether-web-portal/components/toast/useTetherToast";
import { useAuth } from "../../auth/useAuth";
import { AggregatedQueriesResponse, useAggregatedQueries } from "../../hooks/useAggregatedQueries";
import ReportService, {
  ReportEntityNames,
  ReportOptions,
  ReportResult,
  ReportType,
} from "../../services/ReportService";
import { TetherTickPropertySnapshot } from "../../utils/tetherTick";
import { api, NotFoundError } from "../api";
import { applySearchFallback, fetchAllPages, Page, PageParams, pageQueryParams, fetchListSharingFallback, unwrapListResponse, WHOLE_LIST_FALLBACK } from "../pagination";
import { Circuit } from "../../types/Circuit";
import {
  CircuitNode,
  CircuitNodeType,
  CircuitNodeWithUsage,
} from "../../types/CircuitNode";
import { InstallationDetail, InstallationDetailType } from "../../types/InstallationDetail";
import { Organisation } from "../../types/Organisation";
import { OrganisationTag } from "../../types/OrganisationTag";
import { Property } from "../../types/Property";
import { PropertyDetail, PropertyDetailWithReading } from "../../types/PropertyDetail";
import { PropertyInfo, PropertyInfoWithDeviceCounts } from "../../types/PropertyInfo";
import { Room } from "../../types/Room";
import { TimestampRange } from "../../types/TimestampRange";
import { ReadingResolution } from "../readings/hooks";
import { POWER_READING } from "@tether-web-portal/components/property-graph/GraphUtils";
import { UserQueryKeys } from "../users/hooks";
import { OutsideWithReading } from "../../types/OutsideWithReading";
import { buildQueryParam } from "../../utils/buildQueryParams";
import { useNavigate } from "react-router-dom";
import { PropertyDeviceTypes } from "@tether-web-portal/types/PropertyDeviceTypes";
import { DeviceCounts } from "@tether-web-portal/types/DeviceCounts";
import { deviceCountsToDeviceTypes } from "@tether-web-portal/utils/deviceCountsToDeviceTypes";
import { useGetOrganisationFeatureFlag } from "../organisations/hooks";
import { ORGANISATION_FEATURE_FLAGS } from "@tether-web-portal/constants/featureFlags";
import { useGetUserSetting } from "../settings/hooks";
import { UserSettingName } from "@tether-web-portal/types/UserSetting";
import { useAggregatesV2Queries, useAggregatesV2Query } from "@tether-web-portal/hooks/useAggregatesV2Query";

export type MutationProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess?: (data?: any) => void;
  onError?: (error: unknown) => void;
};

export enum QueryKeys {
  PROPERTY = "property",
  PROPERTY_DEVICE_COUNTS = "property-device-counts",
  ROOMS = "room",
  OUTSIDE = "outside",
  CIRCUIT = "circuit",
  PALACE_ID = "palace-id",
  INSTALLATION_DETAIL = "installation-detail",
  TAGS = "property-tags",
  PROPERTY_READINGS = "property-readings",
  PROPERTY_POWER = "property-power",
  PROPERTY_WATER = "property-water",
  PROPERTY_PULSE_READINGS = "property-pulse-readings",
  ROOM_LOCATION_READINGS = "room-location-readings",
  ROOM_LOCATION_READINGS_INSIGHTS = "room-location-insights",
  ROOM_READINGS = "room-readings",
  CIRCUIT_READINGS = "circuit-readings",
  OUTSIDE_READINGS = "outside-readings",
  OUTSIDE_AGGREGATES = "outside-aggregates",
  CIRCUITNODES = "circuit-nodes",
  ORGANISATION_TAGS = "organisation-tags",
  ORGANISATION_ENERGY_BY_TAG = "organisation-energy-by-tag",
  OCCUPANCY_READINGS = "occupancy-readings",
  OUTSIDE_WATER = "outside-water",
  OUTSIDE_PULSE_METER = "outside-pulse-meter",
  PROPERTY_REPORT = "property-report",
  ORGANISATION = "organisation",
  CUSTOMER = "customer",
  DEVICELIST = "device-list",
  PROPERTY_INSIGHT_READINGS = "property-insights-readings",
}

export const enum LocationType {
  RoomLocation = "ROOMLOCATION",
  Outside = "OUTSIDE",
  Circuit = "CIRCUIT",
  CircuitNode = "CIRCUITNODE",
}

/** Rows a type-to-search picker shows. Enough to scroll, few enough to render instantly. */
export const PROPERTY_PICKER_PAGE_SIZE = 50;


/** Endpoint discriminators for the shared flag-off whole list. See {@link WHOLE_LIST_FALLBACK}. */
const PROPERTY_LIST_ENDPOINT = "property/v2/property";
const PROPERTY_INFO_LIST_ENDPOINT = "experience/v1/property/organisation-property-info";

/**
 * The query-cache key a flag-off whole list is shared under.
 *
 * THE ORGANISATION IS PART OF THE KEY, and it is not decoration. This key is also what
 * `fetchListSharingFallback` keys its in-flight map on, and that map is a module-level
 * object that no cache lifecycle touches. An in-SPA organisation switch calls
 * `queryClient.invalidateQueries()` — it does NOT clear the cache, and it cannot reach a
 * module-level Map at all — so with a tenant-agnostic key a whole-property-list request
 * begun before the switch could be handed to a caller in the NEW organisation, and could
 * settle afterwards and `setQueryData` the old organisation's properties back in as fresh.
 * Keyed by organisation, the post-switch caller simply asks a different question.
 */
function wholeListKey(endpoint: string, organisationId: string | undefined): unknown[] {
  return [QueryKeys.PROPERTY, WHOLE_LIST_FALLBACK, endpoint, organisationId];
}

/**
 * One page of properties matching a search term.
 *
 * The replacement for "load every property and filter in the browser". Pickers used a
 * hook that fetched all 1,500 — first unpaged, then by walking every page, which is the
 * same payload with more round trips — and then filtered them locally.
 *
 * Returns a page, not a list, and the caller shows it as matches for what was typed. The
 * `total` says how many matched so a picker can say "showing 50 of 214" instead of
 * pretending the 50 are all of them.
 */
export const useSearchProperties = (search: string, options?: UseQueryOptions<Property[] | Page<Property>>) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organisationId = user?.currentOrganisationId;
  const { data, ...rest } = useQuery<Property[] | Page<Property>>(
    [QueryKeys.PROPERTY, { search, organisationId }],
    () =>
      fetchListSharingFallback<Property>(queryClient, wholeListKey(PROPERTY_LIST_ENDPOINT, organisationId), () =>
        api.get(
          "property/v2/property",
          pageQueryParams({ limit: PROPERTY_PICKER_PAGE_SIZE, offset: 0, search }),
        ),
      ),
    {
      staleTime: 60_000,
      keepPreviousData: true,
      ...options,
    },
  );

  const listResult = unwrapListResponse<Property>(data);

  return {
    ...rest,
    // The search happens HERE while the flag is off — the server ignores it, and this
    // hook's callers deleted their local filters when they started sending it.
    ...applySearchFallback(
      // Capped to one page BEFORE the fallback runs, because applySearchFallback returns
      // early for an EMPTY term — so with the flag off and no term typed, this hook handed
      // its callers the organisation's entire property list. The pickers built from it then
      // rendered one option per property: 1,500 unvirtualised DOM nodes on menu open, under
      // a "showing 50 of 1,500" line that was describing a different array. `total` stays
      // the full count, so the line stays true and the flag-off path now means what the
      // flag-on path means.
      search?.trim()
        ? listResult
        : { ...listResult, items: listResult.items.slice(0, PROPERTY_PICKER_PAGE_SIZE) },
      search,
      // `type` as well, matching what the server's `search` covers. (This row carries no
      // tags; the property-info sibling below does and matches those too.)
      (property) => [
        property.address1,
        property.address2,
        property.addressCity,
        property.addressPostcode,
        property.customPropertyId,
        property.type,
      ],
      PROPERTY_PICKER_PAGE_SIZE,
    ),
  };
};

/**
 * How many properties the organisation has, without fetching any of them.
 *
 * For the screens that only ask "does this organisation have any?" — which used to be
 * answered by downloading all 1,500 and reading `.length`.
 *
 * Asks for one row and reads the SERVER's total. With ORG_SCALE_PAGINATION off the server
 * ignores the paging parameters and answers with the whole array, so the count is still
 * right; it is just not cheap yet, and it gets cheap the moment the flag flips.
 */
export const usePropertyCount = (options?: UseQueryOptions<Property[] | Page<Property>>) => {
  const { data, ...rest } = useQuery<Property[] | Page<Property>>(
    [QueryKeys.PROPERTY, { count: true }],
    () => api.get("property/v2/property", pageQueryParams({ limit: 1, offset: 0 })),
    {
      staleTime: Infinity,
      ...options,
    },
  );

  const { total } = unwrapListResponse<Property>(data);
  return { ...rest, count: total };
};

/**
 * Every property id in the organisation, fetched on demand.
 *
 * NOT a hook, and deliberately awkward to reach for. Its one caller is the export builder,
 * which has to expand an "Organisation" scope into explicit per-property filters because
 * that is what the export API takes.
 *
 * The point is WHEN it runs: at submit, on an explicit action whose whole purpose is the
 * entire organisation — not on page load for everyone who opens the exports screen, which
 * is what calling a fetch-everything hook in the component body did.
 */
export async function fetchAllPropertyIds(): Promise<string[]> {
  const properties = await fetchAllPages<Property>((page) => api.get("property/v2/property", page));
  return properties.map((property) => property.id);
}

/**
 * Every property in the organisation — the ONE place that still loads all of them.
 *
 * The portfolio map plots a pin per property. Showing the first page would plot 50 pins
 * out of 1,500 with nothing on screen to say so, which is the failure this whole exercise
 * is about; a map that quietly omits most of the portfolio is worse than a slow one.
 *
 * So this is the single remaining walk in the portal, and it is named for what makes it
 * legitimate. Everything else got a page, a search or an endpoint that answers the
 * question directly. Phase 3 replaces this with a coordinates-only lens plus clustering,
 * which is what actually removes the payload — until then the cost is on the wire, not on
 * the database (the query is the same two either way).
 *
 * If you are about to call this for anything other than the map: don't. Use
 * {@link useSearchProperties} for a picker, {@link useGetPropertiesByIds} for known rows,
 * {@link usePropertyCount} for "are there any", or ask for an endpoint.
 */
export const useGetAllPropertiesForMap = (options?: UseQueryOptions<Property[]>) =>
  useQuery<Property[]>(
    [QueryKeys.PROPERTY, { allPropertiesForMap: true }],
    () => fetchAllPages<Property>((page) => api.get("property/v2/property", page)),
    {
      ...options,
      staleTime: Infinity,
    },
  );

/**
 * One page of property info matching a search term.
 *
 * The property-info counterpart to {@link useSearchProperties}, for pickers that show
 * tags or device counts. The API's `search` covers address1, address2, city, postcode,
 * customPropertyId, type and property tags — the same fields the callers of this used to
 * filter on locally, after downloading all 1,500.
 */
export const useSearchOrganisationPropertyInfo = (
  search: string,
  options?: UseQueryOptions<PropertyInfo[] | Page<PropertyInfo>>,
) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organisationId = user?.currentOrganisationId;
  const { data, ...rest } = useQuery<PropertyInfo[] | Page<PropertyInfo>>(
    [QueryKeys.PROPERTY, { orgPropertyInfo: true, search, organisationId }],
    () =>
      fetchListSharingFallback<PropertyInfo>(queryClient, wholeListKey(PROPERTY_INFO_LIST_ENDPOINT, organisationId), () =>
        api.get("/experience/v1/property/organisation-property-info", {
          useV2: true,
          skipDeviceCounts: true,
          ...pageQueryParams({ limit: PROPERTY_PICKER_PAGE_SIZE, offset: 0, search }),
        }),
      ),
    {
      staleTime: 60_000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

  return {
    ...rest,
    ...applySearchFallback(
      unwrapListResponse<PropertyInfo>(data),
      search,
      // The same fields the SERVER's `search` covers, so flipping the flag can only widen
      // a result, never narrow one. Tags in particular: the local filter this replaced
      // matched them, so a user typing a property tag in the alert picker went from
      // matching to being told `No properties match "Retail"` — a false statement about
      // their own organisation, invisible to anyone testing with the flag ON.
      (property) => [
        property.address1,
        property.address2,
        property.addressCity,
        property.addressPostcode,
        property.customPropertyId,
        property.type,
        ...(property.propertyTags ?? []),
      ],
      PROPERTY_PICKER_PAGE_SIZE,
    ),
  };
};

/**
 * Narrow a list response to the ids that were asked for.
 *
 * On the paged path the server already applied the `propertyIds` filter and this is a
 * no-op. While the flag is off it ignored the filter and answered with the whole
 * organisation, which is the case this exists for.
 */
function keepRequestedIds<T extends { id: string }>(
  items: T[],
  ids: string[],
  serverPaged: boolean,
): T[] {
  if (serverPaged) {
    return items;
  }

  const wanted = new Set(ids);
  return items.filter((item) => wanted.has(item.id));
}

/**
 * Fetch a set of ids in batches the request line can actually carry.
 *
 * The ids go in the QUERY STRING, one `propertyIds=<uuid>` pair each — about 50 bytes
 * apiece. The org-wide Alerts page builds its id list from a week of alert history, which
 * on a 1,500-property organisation is easily 300 distinct properties: ~15KB of request
 * line, past the 8KB most proxies enforce, so the request 414s and the alerts table
 * renders its error state. Below that the failure is quieter — the server caps
 * `propertyIds` at 200 and 400s, or (with the cap only on `limit`) answers with the first
 * 200 rows, which the caller renders as if it were all of them.
 *
 * SO THE BATCH IS SIZED BY THE REQUEST LINE, NOT BY THE PAGE LIMIT. Batching at
 * MAX_PAGE_LIMIT keeps every request one the SERVER will accept — 200 is its own cap on
 * `propertyIds` — but it walks straight into the proxy limit above: `propertyIds=` plus a
 * 36-character UUID plus `&` is 50 bytes, so 200 ids is ~10KB of query string before the
 * path and the other parameters. That is over the 8KB this comment was written to avoid.
 *
 * 100 ids is ~5KB, which leaves room for the endpoint, `limit` and `skipDeviceCounts`. It
 * is the same number the alert-settings scope-id batching settled on, for the same reason
 * and after the same arithmetic.
 */
const MAX_IDS_PER_REQUEST = 100;
async function fetchByIdsInBatches<T>(
  ids: string[],
  fetchBatch: (batch: string[]) => Promise<T[] | Page<T>>,
): Promise<T[]> {
  const results: T[] = [];

  for (let start = 0; start < ids.length; start += MAX_IDS_PER_REQUEST) {
    const batch = ids.slice(start, start + MAX_IDS_PER_REQUEST);
    const { items, serverPaged } = unwrapListResponse<T>(await fetchBatch(batch));

    // Flag off: the server ignored `propertyIds` and answered with the whole
    // organisation. That response already contains every id, so one batch is the answer.
    if (!serverPaged) {
      return items;
    }

    results.push(...items);
  }

  return results;
}

/**
 * Full properties for a known set of ids, OUTSIDE a component.
 *
 * The hook below is the normal way to ask. This exists for the CSV export, which resolves
 * labels for an id set it only learns while the download is running — every device across
 * every page, not the page on screen.
 */
export function fetchPropertiesByIds(ids: string[]): Promise<Property[]> {
  if (ids.length === 0) {
    return Promise.resolve([]);
  }

  return fetchByIdsInBatches<Property>(
    ids,
    (batch) =>
      api.get("property/v2/property", {
        propertyIds: batch,
        limit: String(batch.length),
      }),
  ).then((properties) => keepRequestedIds(properties, ids, false));
}

/**
 * Full properties for a KNOWN set of ids, in one request.
 *
 * Uses the list contract's `propertyIds` filter rather than one request per id, and rather
 * than loading the whole organisation and filtering client-side — which is what the
 * covid-care dashboard did to put an address on twenty tiles.
 *
 * Capped at the contract's 200 ids; a caller with more than that wants a page, not a
 * filter.
 */
export const useGetPropertiesByIds = (
  ids: string[],
  options?: UseQueryOptions<Property[] | Page<Property>>,
) => {
  const sorted = useMemo(() => [...ids].sort(), [ids]);

  const { data, ...rest } = useQuery<Property[]>(
    [QueryKeys.PROPERTY, { byIds: sorted }],
    () =>
      fetchByIdsInBatches<Property>(
        sorted,
        (batch) =>
          api.get("property/v2/property", {
            propertyIds: batch,
            limit: String(batch.length),
          }),
      ),
    {
      enabled: sorted.length > 0,
      staleTime: Infinity,
      ...(options as UseQueryOptions<Property[]>),
    },
  );

  // The batched fetch reports whether the server filtered; a flag-off answer is the whole
  // organisation and still needs narrowing here.
  return { ...rest, properties: keepRequestedIds(data ?? [], sorted, false) };
};

export const useGetProperty = (id: string | undefined, options?: UseQueryOptions<Property>) => {
  const { data: property, ...rest } = useQuery<Property>(
    [QueryKeys.PROPERTY, id],
    () => api.get(`property/v2/property/${id}`),
    {
      enabled: Boolean(id),
      staleTime: Infinity,
      ...options,
    },
  );
  return { property, ...rest };
};

export function useGetInstallationDetails(id?: string) {
  return useQuery<InstallationDetail<number | boolean | string>[]>(
    [QueryKeys.INSTALLATION_DETAIL, id],
    () => api.get(`v2/installation/${id}/detail`),
    {
      enabled: Boolean(id),
      staleTime: Infinity,
      retry: false,
    },
  );
}

export function useGetInstallationDetail<T>(id: string | undefined, name: string | undefined) {
  const { data: installationDetails, ...rest } = useGetInstallationDetails(id);

  const targetDetail = useMemo<InstallationDetail<T> | undefined>(
    () => (installationDetails ?? []).find((x) => x.name === name) as InstallationDetail<T>,
    [installationDetails, name],
  );
  return {
    data: targetDetail,
    ...rest,
  };
}

export const useGetPropertyDeviceCounts = (id: string) => {
  const { data: deviceCounts, ...rest } = useQuery<DeviceCounts, Error>(
    [QueryKeys.PROPERTY_DEVICE_COUNTS, id],
    () => api.get(`experience/v1/property/${id}/device-counts`),
    {
      enabled: Boolean(id),
      staleTime: Infinity,
    },
  );
  const deviceTypes = useMemo((): PropertyDeviceTypes => {
    if (!deviceCounts) return new Set();
    return deviceCountsToDeviceTypes(deviceCounts);
  }, [deviceCounts]);
  return { deviceCounts, deviceTypes, ...rest };
};

export const useGetPropertyDetail = (id: string, options?: UseQueryOptions<PropertyDetail, Error>) => {
  const { data: propertyInfo, ...rest } = useQuery<PropertyDetail, Error>(
    [QueryKeys.PROPERTY, id, { propertyInfo: true, skipLastSamples: true }],
    () => api.get(`experience/v1/property/property-info/${id}`, { skipLastSamples: true, useV2: false }),
    {
      enabled: Boolean(id),
      staleTime: Infinity,
      ...options,
    },
  );
  return { propertyInfo, ...rest };
};

export const useGetPropertyDetailWithReadings = (
  id: string,
  options?: UseQueryOptions<PropertyDetailWithReading, Error>,
) => {
  const { data: propertyInfo, ...rest } = useQuery<PropertyDetailWithReading, Error>(
    [QueryKeys.PROPERTY, id, { propertyInfo: true }],
    () => api.get(`experience/v1/property/property-info/${id}`, { useV2: false }),
    {
      enabled: Boolean(id),
      staleTime: Infinity,
      ...options,
    },
  );
  return { propertyInfo, ...rest };
};

interface PropertyDetailResult {
  id: string;
  propertyInfo: PropertyDetailWithReading;
}

export const useGetPropertyDetailForMultipleProperties = (
  ids: string[],
  options?: UseQueryOptions<PropertyDetailWithReading, Error, PropertyDetailResult>,
) => {
  const { data: propertyInfos, ...rest } = useAggregatedQueries<
    PropertyDetailWithReading,
    Error,
    PropertyDetailResult
  >(
    ids.map((id) => {
      return {
        queryKey: [QueryKeys.PROPERTY, id, { propertyInfo: true }],
        queryFn: () => api.get(`experience/v1/property/property-info/${id}`),
        staleTime: Infinity,
        enabled: Boolean(id),
        select: (propertyInfo: PropertyDetailWithReading) => ({
          id,
          propertyInfo,
        }),
        ...options,
      };
    }),
  );

  return { propertyInfos, ...rest };
};

export const useGetPropertyTetherTickSnapshots = (id: string | undefined) => {
  const isStaging = window.location.hostname.includes("staging");

  const apiRoot = isStaging
    ? "https://complianceapi.staging.tetherhq.com"
    : "https://complianceapi.tetherhq.com"; //

  const { data: snapshots, ...rest } = useQuery<TetherTickPropertySnapshot[]>(
    [QueryKeys.PROPERTY, id, { tetherTick: true }],
    () => api.get(apiRoot + `/snapshot/${id}`),
    {
      enabled: Boolean(id),
      staleTime: Infinity,
    },
  );
  return { snapshots, ...rest };
};

export const useGetRooms = (propertyId: string) => {
  const { data: rooms, ...rest } = useQuery<Room[]>(
    [QueryKeys.ROOMS, propertyId],
    () => api.get(`property/v2/room?propertyId=${propertyId}`),
    {
      enabled: Boolean(propertyId),
      staleTime: Infinity,
    },
  );
  return { rooms, ...rest };
};

export interface DeleteRoomInput {
  roomId: string;
  propertyId: string;
}

export const useDeleteRoomMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleDeleteRoom = async ({ input }: { input: DeleteRoomInput }) => {
    try {
      const locations = await api.get("property/v2/roomlocation", { roomId: input.roomId });
      for (const location of locations) {
        await api.del(`property/v2/roomlocation/${location.id}`);
      }
    } catch (e) {
      queryClient.invalidateQueries([QueryKeys.PROPERTY, input.propertyId]);
      queryClient.invalidateQueries([QueryKeys.ROOMS, input.propertyId]);
      throw e;
    }
    return api.del(`property/v2/room/${input.roomId}`);
  };

  return useMutation(handleDeleteRoom, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to delete room",
        description: e.message,
      });
    },
    onSuccess: (data, { input }) => {
      queryClient.invalidateQueries([QueryKeys.PROPERTY, input.propertyId]);
      queryClient.invalidateQueries([QueryKeys.ROOMS, input.propertyId]);
      if (onSuccess) {
        onSuccess();
      }
      successToast({
        title: "Successfully deleted room",
      });
    },
  });
};

export interface DeleteLocationInput {
  locationId: string;
  propertyId: string;
  locationType: LocationType;
}

export const useDeleteLocationMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleDeleteLocation = async ({ input }: { input: DeleteLocationInput }) => {
    const { locationId, locationType } = input;
    switch (locationType) {
      case LocationType.RoomLocation:
        return api.del(`property/v2/roomlocation/${locationId}`);
      case LocationType.Outside:
        return api.del(`property/v2/outside/${locationId}`);
      case LocationType.Circuit:
        return api.del(`property/v2/circuit/${locationId}`);
      case LocationType.CircuitNode:
        return api.del(`property/v2/circuitnode/${locationId}`);
      default:
        throw new Error(`Unknown Location Type: ${locationType}`);
    }
  };

  return useMutation(handleDeleteLocation, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to delete location",
        description: e.message,
      });
    },
    onSuccess: (data, { input }) => {
      queryClient.invalidateQueries([QueryKeys.PROPERTY, input.propertyId]);
      queryClient.invalidateQueries([QueryKeys.ROOMS, input.propertyId]);
      queryClient.invalidateQueries([QueryKeys.CIRCUITNODES, input.propertyId]);
      queryClient.invalidateQueries([QueryKeys.CIRCUIT, input.propertyId]);
      if (onSuccess) {
        onSuccess();
      }
      successToast({
        title: "Successfully deleted location",
      });
    },
  });
};

export const useGetRoom = (roomId: string, options?: UseQueryOptions<Room, Error>) => {
  const { data: room, ...rest } = useQuery<Room, Error>(
    [QueryKeys.ROOMS, roomId],
    () => api.get(`property/v2/room/${roomId}`),
    {
      enabled: Boolean(roomId),
      staleTime: Infinity,
      ...options,
    },
  );
  return { room, ...rest };
};

export const useGetOutside = (outsideId: string, options?: UseQueryOptions<OutsideWithReading, Error>) => {
  const { data: outside, ...rest } = useQuery<OutsideWithReading, Error>(
    [QueryKeys.OUTSIDE, outsideId],
    () => api.get(`property/v2/outside/${outsideId}`),
    {
      enabled: Boolean(outsideId),
      staleTime: Infinity,
      ...options,
    },
  );
  return { outside, ...rest };
};

export const useGetOutsidesForProperty = (
  propertyId: string,
  options?: UseQueryOptions<OutsideWithReading[], Error>,
) => {
  const { data: outsides, ...rest } = useQuery<OutsideWithReading[], Error>(
    [QueryKeys.OUTSIDE, propertyId],
    () => api.get("property/v2/outside", { propertyId }),
    {
      enabled: Boolean(propertyId),
      staleTime: Infinity,
      ...options,
    },
  );
  return { outsides, ...rest };
};

export const useGetCircuitNodesForProperty = (
  propertyId: string,
) => {
  const { data: circuitNodes, ...rest } = useQuery<Circuit[], Error>(
    [QueryKeys.CIRCUITNODES, propertyId],
    () => api.get("property/v2/circuitNode", { propertyId }),
  );
  return { circuitNodes, ...rest };
};

export const useGetCircuit = (circuitId: string, options?: UseQueryOptions<Circuit, Error>) => {
  const { data: circuit, ...rest } = useQuery<Circuit, Error>(
    [QueryKeys.CIRCUIT, circuitId],
    () => api.get(`property/v2/circuit/${circuitId}`),
    {
      enabled: Boolean(circuitId),
      staleTime: Infinity,
      ...options,
    },
  );
  return { circuit, ...rest };
};

export const useGetCircuitNode = (
  circuitNodeId: string | undefined,
  options?: UseQueryOptions<CircuitNode, Error>,
) => {

  const { data: circuitNode, ...rest } = useAggregatesV2Query<CircuitNode, Error>(
    [QueryKeys.CIRCUITNODES, circuitNodeId],
    (_, useV2Aggregates) => api.get(`property/v2/circuitnode/${circuitNodeId}`, {
      ...(useV2Aggregates && { useV2: true })
    }),
    {
      enabled: Boolean(circuitNodeId),
      staleTime: Infinity,
      ...options,
    },
  );
  return { circuitNode, ...rest };
};

export const useGetStreetViewImage = (propertyId: string) => {
  const queryClient = useQueryClient();
  const key = ["streetViewImage", propertyId];
  const { data: imageUrl, ...rest } = useQuery<{ imageUrl: string }>(
    key,
    () => api.get(`property/v2/property/${propertyId}/streetview`),
    {
      enabled: Boolean(propertyId),
      retry: false,
      staleTime: Infinity,
      onError: (error) => {
        if (error as NotFoundError) {
          queryClient.setQueryData(key, undefined);
        }
      },
    },
  );
  return { imageUrl, ...rest };
};

export interface DeletePropertyInput {
  id: string;
}

export const useDeletePropertyMutation = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    ({ input }: { input: DeletePropertyInput }) => api.del(`property/v2/property/${input.id}`),
    {
      onError: () => {
        toast({
          title: "Unable to delete property",
          status: "error",
          isClosable: true,
          position: "top-right",
        });
      },
      onSuccess: (data, { input }) => {
        navigate("/", { replace: true });
        queryClient.invalidateQueries([QueryKeys.PROPERTY, { orgPropertyInfo: true }]);
      },
    },
  );
};

export type CreateRoomInput = {
  type: string;
  floorIndex: number;
  name: string;
  color: string;
  propertyId: string;
  customId?: string | null;
  capacity?: number | null;
  tags?: string[];
};

export const useCreateRoomMutation = (onCreated?: () => void) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const handleCreateRoom = async ({ input }: { input: CreateRoomInput }) => {
    const room = await api.post("property/v2/room", input);

    // Hopefully temporary hack to create a room location when creating a room,
    // without this it won't be visible in the mobile app
    try {
      await api.post("property/v2/roomLocation", { ...input, roomId: room.id });
    } catch (e) {
      // Don't throw an error here as it will have created the room already and
      // things'll break in a confusing way if they try and click create again.
      // Lettuce spray this doesn't happen...
    }
  };

  return useMutation(handleCreateRoom, {
    onError: () => {
      toast({
        title: "Unable to create area",
        status: "error",
        isClosable: true,
        position: "top-right",
      });
    },
    onSuccess: (data, { input }) => {
      toast({
        title: "Area Created",
        status: "success",
        isClosable: true,
        position: "top-right",
      });
      // Invalidate and refetch
      queryClient.resetQueries([QueryKeys.PROPERTY, input.propertyId, { propertyInfo: true }]);
      queryClient.invalidateQueries([QueryKeys.ROOMS, input.propertyId]);
      queryClient.invalidateQueries([QueryKeys.PROPERTY, input.propertyId]);
      if (onCreated) {
        onCreated();
      }
    },
  });
};

export const useUpdateRoomMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(({ input }: { input: Room }) => api.put(`property/v2/room/${input.id}`, input), {
    onError: () => {
      toast({
        title: "Unable to update area",
        status: "error",
        isClosable: true,
        position: "top-right",
      });
    },
    onSuccess: (data, { input }) => {
      toast({
        title: "Area Updated",
        status: "success",
        isClosable: true,
        position: "top-right",
      });
      // Invalidate and refetch
      queryClient.resetQueries([QueryKeys.PROPERTY, input.propertyId, { propertyInfo: true }]);
      queryClient.invalidateQueries([QueryKeys.ROOMS, input.id]);
      queryClient.invalidateQueries([QueryKeys.ROOMS, input.propertyId]);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export const useUpdateLocationMutation = (type: LocationType) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    ({
      input,
    }: {
      input: { id: string; name?: string; color?: string; roomId?: string };
      propertyId: string;
      onSuccess?: () => void;
    }) => api.put(`property/v2/${type.toLowerCase()}/${input.id}`, input),
    {
      onError: () => {
        toast({
          title: "Unable to update",
          status: "error",
          isClosable: true,
          position: "top-right",
        });
      },
      onSuccess: (data, { input, propertyId, onSuccess }) => {
        toast({
          title: "Updated.",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        // Invalidate and refetch
        queryClient.invalidateQueries([QueryKeys.PROPERTY, propertyId]);
        queryClient.invalidateQueries([QueryKeys.ROOMS, input.roomId]);
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );
};

interface CircuitMutationInput {
  id: string;
  propertyId: string;
  name: string;
  color: string;
  phase: number | null;
}

export const useUpdateCircuitMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    ({ input }: { input: CircuitMutationInput; propertyId: string; onSuccess?: () => void }) =>
      api.put(`property/v2/circuit/${input.id}`, input),
    {
      onError: () => {
        toast({
          title: "Unable to update",
          status: "error",
          isClosable: true,
          position: "top-right",
        });
      },
      onSuccess: (data, { input, propertyId, onSuccess }) => {
        toast({
          title: "Updated.",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        // Invalidate and refetch
        queryClient.invalidateQueries([QueryKeys.PROPERTY, propertyId]);
        queryClient.invalidateQueries([QueryKeys.CIRCUITNODES, propertyId]);
        queryClient.invalidateQueries([QueryKeys.CIRCUIT, input.id]);
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );
};

/**
 * Every property's info in the organisation — the second and last full load in the portal.
 *
 * The management report is a report OVER the whole organisation: it walks every property,
 * optionally narrowed by tag, and renders a document. Showing the first page would produce
 * a report that silently covers 50 properties out of 1,500 and says nothing about it,
 * which for a document someone sends to a customer is worse than a slow page.
 *
 * Named for its one legitimate caller, like {@link useGetAllPropertiesForMap}. Everything
 * else that used to load the whole organisation now takes a page
 * ({@link useGetPagedOrganisationPropertyInfo}), a search
 * ({@link useSearchOrganisationPropertyInfo}), specific ids
 * ({@link useGetPropertyInfoByIds}) or a purpose-built endpoint.
 */
export const useGetAllOrganisationPropertyInfoForReport = () => {
  return useQuery<PropertyInfoWithDeviceCounts[]>(
    [QueryKeys.PROPERTY, { orgPropertyInfo: true, forReport: true }],
    () =>
      fetchAllPages<PropertyInfoWithDeviceCounts>((page) =>
        api.get("/experience/v1/property/organisation-property-info", {
          useV2: true,
          // The report reads propertyTags and ids; it has never read deviceCounts. Its
          // predecessor sent this and the conversion dropped it, making the API resolve
          // counts for all 1,500 properties on every one of the walk's requests.
          skipDeviceCounts: true,
          ...page,
        }),
      ),
    {
      staleTime: Infinity,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
    },
  );
};

/**
 * One page of the organisation's properties with their device counts — the org-scale
 * Phase 2 list contract.
 *
 * Returns a {@link ListResult}, not an array, and the caller MUST branch on
 * `serverPaged`. While the API's ORG_SCALE_PAGINATION flag is off, the server ignores the
 * paging parameters and answers with the legacy full array; this hook reports that
 * honestly (`serverPaged: false`, every row present) so the page can keep doing exactly
 * what it does today — client-side paging, filtering and sorting.
 *
 * That is what makes this safe to merge and deploy before the flag is flipped, and what
 * makes the flip reversible without a frontend deploy.
 *
 * `keepPreviousData` so moving between pages does not blank the table; the paging
 * controls stay usable while the next page is in flight.
 */
export const useGetPagedOrganisationPropertyInfo = (params: PageParams) => {
  const query = pageQueryParams(params);

  const { data, ...rest } = useQuery<PropertyInfoWithDeviceCounts[] | Page<PropertyInfoWithDeviceCounts>>(
    [QueryKeys.PROPERTY, { orgPropertyInfo: true, paged: query }],
    () => api.get("/experience/v1/property/organisation-property-info", { useV2: true, ...query }),
    {
      staleTime: Infinity,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return { ...rest, ...unwrapListResponse<PropertyInfoWithDeviceCounts>(data) };
};

/**
 * The property info for a KNOWN set of ids — the pinned-properties panel.
 *
 * Pinned properties have to appear whichever page of the list is showing, so they cannot
 * come out of the paged response. Without a targeted request the panel has exactly two
 * options and both are wrong: load the whole organisation again, undoing the paging; or
 * show only the pins that happen to be on the current page, which looks like the user's
 * pins have silently disappeared.
 *
 * Bounded by the user's own pin count, not by organisation size.
 *
 * While the API's pagination flag is off, `propertyIds` is ignored and the whole list
 * comes back — so the filter is applied here too. That is the documented degradation, not
 * a bug: the endpoint answers exactly as it does today and the client narrows.
 */
export const useGetPropertyInfoByIds = (propertyIds: string[], skipDeviceCounts = false) => {
  const ids = useMemo(() => [...propertyIds].sort(), [propertyIds]);

  const { data, ...rest } = useQuery<PropertyInfoWithDeviceCounts[]>(
    [QueryKeys.PROPERTY, { orgPropertyInfo: true, propertyIds: ids, skipDeviceCounts }],
    () =>
      fetchByIdsInBatches<PropertyInfoWithDeviceCounts>(ids, (batch) =>
        api.get("/experience/v1/property/organisation-property-info", {
          useV2: true,
          propertyIds: batch,
          limit: String(batch.length),
          // Only when the caller says it does not read them. The alerts screen renders
          // addresses and tags, and resolving counts it never shows costs the server four
          // grouped queries per page. Defaults to sending them, because this hook's name
          // promises them and one caller does read them.
          ...(skipDeviceCounts ? { skipDeviceCounts: true } : {}),
        }),
      ),
    {
      enabled: ids.length > 0,
      staleTime: Infinity,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
    },
  );

  return { ...rest, properties: keepRequestedIds(data ?? [], ids, false) };
};

export type DeviceAssignmentTargetType = "room" | "circuitNode" | "outside";

export interface DeviceListObject {
  id: string;
  shortSerial: string;
  serialNumber: string;
  type: string;
  sku: string;
  firmwareVersion: string;
  hardwareVersion: string;
  subDeviceIndex: null | number;
  createdAt: string;
  network: string;
  lastSample: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sample: any;
    timestamp: number;
    deviceId: string;
  };
  signalStrengthPercentage: {
    rssi: number;
    percentage: number;
    timestamp?: string;
  };
  deviceAssignment?: {
    propertyId: string;
    targetId: string;
    targetType: DeviceAssignmentTargetType;
  };
  gatewayStatus?: {
    lastUplinkReceivedAt: string;
    isConnected: boolean;
  };
}

interface DeviceListResponse {
  devices: DeviceListObject[];
  page: number;
  /**
   * Devices matching every filter, ignoring the page window.
   *
   * The count a paged table needs to say "1-50 of 8,880" and to reach the last page.
   * Optional because an API deployed before this field still answers without it — the
   * table falls back to page numbers alone rather than inventing a row count.
   */
  total?: number;
  totalPages: number;
}

interface DeviceListSort {
  type?: string;
  page?: number;
  /** Page size. Defaults to 50 server-side, capped at 200. */
  limit?: number;
  sort?: string; // 'serialNumber' | 'battery' | 'signal' | 'lastSampleTimestamp';
  desc?: boolean;
  propertyId?: string;
  status?: string;
  battery?: string;
  signalStrength?: string;
  /** Free text across serial number, id, manufacturing id, sku and type. */
  search?: string;
}

export const useDeviceListExperience = ({
  page,
  limit,
  type,
  sort,
  desc,
  propertyId,
  status,
  battery,
  signalStrength,
  search,
}: DeviceListSort) => {
  const queryParam = buildQueryParam({ page, limit, sort, desc, type, propertyId, status, battery, signalStrength, search });
  return useQuery<DeviceListResponse>(
    [QueryKeys.DEVICELIST, { queryParam }],
    () => api.get("/experience/v1/device?" + queryParam),
    {
      staleTime: 60000,
      // The rows stay while the next page loads, so paging dims the table instead of
      // emptying it. `isLoading` is false throughout under this — callers must read
      // `isFetching` to know a request is in flight.
      keepPreviousData: true,
    },
  );
};

// export const useDeviceListExperienceAllPages = () => {

//   const [page, setPage] = useState(1);
//   const [hasMore, setHasMore] = useState(true);
//   const [devices, setDevices] = useState<DeviceListObject[]>([]);
//   const [isLoading, setIsLoading] = useState(false);

//   const fetchPage = async (page: number) => {
//     setIsLoading(true);
//     const queryParam = buildQueryParam({ page });
//     const response: DeviceListResponse = await api.get("/experience/v1/device?" + queryParam);
//     setIsLoading(false);
//     setDevices([...devices, ...response.devices]);
//     setHasMore(response.devices?.length > 0);
//     setPage(page + 1);
//   }

//   useEffect(() => {
//     if (hasMore) {
//       fetchPage(page);
//     }
//   }, [page, hasMore]);

//   return {
//     devices,
//     isLoading: isLoading || hasMore
//   }
// }

export interface CreatePropertyInput {
  type: string;
  customPropertyId?: string;
  address1?: string;
  address2?: string;
  addressCity?: string;
  addressCountry?: string;
  addressPostcode?: string;
  latitude?: number;
  longitude?: number;
  floorArea?: number;
  costPerKwh?: number;
  co2PerKwh?: number;
  gasCostPerCubicMetre?: number;
  gasCalorificValue?: number;
  tags?: string[];
  timezone: string;
}

export const useCreatePropertyMutation = (onPropertyCreated?: (id: string) => void) => {
  const queryClient = useQueryClient();

  const createProperty = async ({ input }: { input: CreatePropertyInput }) => {
    const { floorArea, costPerKwh, co2PerKwh, gasCostPerCubicMetre, gasCalorificValue, tags, ...rest } = input;

    try {
      const res = await api.post(`property/v2/property`, rest);
      const additionalPromises = [];

      if (floorArea) {
        additionalPromises.push(
          api.post(`v2/installation/${res.id}/detail/${InstallationDetailType.FLOOR_AREA}`, {
            type: "number",
            value: floorArea,
          }),
        );
      }
      if (costPerKwh) {
        additionalPromises.push(
          api.post(`v2/installation/${res.id}/detail/${InstallationDetailType.COST_PER_KWH}`, {
            type: "number",
            value: costPerKwh,
          }),
        );
      }
      if (co2PerKwh) {
        additionalPromises.push(
          api.post(`v2/installation/${res.id}/detail/${InstallationDetailType.CO2_PER_KWH}`, {
            type: "number",
            value: co2PerKwh,
          }),
        );
      }

	  if (gasCostPerCubicMetre) {
		  additionalPromises.push(
			  api.post(`v2/installation/${res.id}/detail/${InstallationDetailType.GAS_COST_PER_CUBIC_METRE}`, {
				  type: "number",
				  value: gasCostPerCubicMetre || 0,
			  }),
		  );
	  }

      if (gasCalorificValue) {
        additionalPromises.push(
          api.post(`v2/installation/${res.id}/detail/${InstallationDetailType.GAS_CALORIFIC_VALUE}`, {
            type: "number",
            value: gasCalorificValue,
          }),
        );
      }

      if (tags) {
        tags.forEach((tag) =>
          additionalPromises.push(api.put(`property/v2/property/${res.id}/tag`, { tag })),
        );
      }

      await Promise.all(additionalPromises);

      return res;
    } catch (e) {
      console.log("Unable to update property");
    }
  };

  return useMutation(createProperty, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(QueryKeys.PROPERTY);
      queryClient.invalidateQueries(QueryKeys.TAGS);
      if (onPropertyCreated) {
        onPropertyCreated(data.id);
      }
    },
  });
};

interface PropertyMutationProps extends Property {
  tagsAdded?: string[];
  tagsRemoved?: string[];
}

export const useUpdatePropertyMutation = (onSuccess?: (id: string) => void) => {
  const queryClient = useQueryClient();
  const { errorToast } = useTetherToast();

  const updateProperty = async ({ input }: { input: PropertyMutationProps }) => {
    const { floorArea, costPerKwh, co2PerKwh, tagsAdded, tagsRemoved, gasCostPerCubicMetre, gasCalorificValue, ...rest } = input;
    const propertyId = rest.id;

    const res = await api.put(`property/v2/property/${propertyId}`, rest);

    const additionalPromises = [];

    if (floorArea) {
      additionalPromises.push(
        api.post(`v2/installation/${propertyId}/detail/${InstallationDetailType.FLOOR_AREA}`, {
          type: "number",
          value: floorArea || 0,
        }),
      );
    } else {
      additionalPromises.push(
        api.del(`v2/installation/${propertyId}/detail/${InstallationDetailType.FLOOR_AREA}`),
      );
    }

    if (costPerKwh) {
      additionalPromises.push(
        api.post(`v2/installation/${propertyId}/detail/${InstallationDetailType.COST_PER_KWH}`, {
          type: "number",
          value: costPerKwh || 0,
        }),
      );
    } else {
      additionalPromises.push(
        api.del(`v2/installation/${propertyId}/detail/${InstallationDetailType.COST_PER_KWH}`),
      );
    }

    if (co2PerKwh) {
      additionalPromises.push(
        api.post(`v2/installation/${propertyId}/detail/${InstallationDetailType.CO2_PER_KWH}`, {
          type: "number",
          value: co2PerKwh || 0,
        }),
      );
    } else {
      additionalPromises.push(
        api.del(`v2/installation/${propertyId}/detail/${InstallationDetailType.CO2_PER_KWH}`),
      );
    }

    if (gasCostPerCubicMetre) {
      additionalPromises.push(
        api.post(`v2/installation/${propertyId}/detail/${InstallationDetailType.GAS_COST_PER_CUBIC_METRE}`, {
          type: "number",
          value: gasCostPerCubicMetre || 0,
        }),
      );
    } else {
      additionalPromises.push(
        api.del(`v2/installation/${propertyId}/detail/${InstallationDetailType.GAS_COST_PER_CUBIC_METRE}`),
      );
    }

    if (gasCalorificValue) {
      additionalPromises.push(
        api.post(`v2/installation/${propertyId}/detail/${InstallationDetailType.GAS_CALORIFIC_VALUE}`, {
          type: "number",
          value: gasCalorificValue,
        }),
      );
    } else {
      additionalPromises.push(
        api.del(`v2/installation/${propertyId}/detail/${InstallationDetailType.GAS_CALORIFIC_VALUE}`),
      );
    }

    if (tagsAdded) {
      tagsAdded.forEach((tag) =>
        additionalPromises.push(api.put(`property/v2/property/${propertyId}/tag`, { tag })),
      );
    }
    if (tagsRemoved) {
      tagsRemoved.forEach((tag) =>
        additionalPromises.push(api.del(`property/v2/property/${propertyId}/tag/${tag}`)),
      );
    }

    await Promise.all(additionalPromises);
    return res;
  };

  return useMutation(updateProperty, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(QueryKeys.PROPERTY);
      queryClient.invalidateQueries([QueryKeys.INSTALLATION_DETAIL, data.id]);
      queryClient.invalidateQueries(QueryKeys.TAGS);
      if (onSuccess) {
        onSuccess(data.id);
      }
    },
    onError: (error: Error) => {
      errorToast({
        title: "Unable to update property",
        description: error.message,
      });
    },
  });
};

interface UpdateInstallationDetailRequest {
  propertyId: string;
  name: string;
  type: "string" | "boolean" | "number";
  value: string | boolean | number;
}
export const useUpdateInstallationDetailMutation = (
  options?: UseMutationOptions<InstallationDetail, Error, UpdateInstallationDetailRequest>,
) => {
  const queryClient = useQueryClient();

  const updateInstallationDetail = async ({
    propertyId,
    name,
    ...request
  }: UpdateInstallationDetailRequest) => {
    return api.post(`v2/installation/${propertyId}/detail/${name}`, request);
  };

  return useMutation(updateInstallationDetail, {
    ...options,
    onSuccess: (response, input, context) => {
      queryClient.invalidateQueries([QueryKeys.INSTALLATION_DETAIL, input.propertyId, input.name]);
      if (options?.onSuccess) {
        options.onSuccess(response, input, context);
      }
    },
  });
};

export default function useCreatePropertyReport() {
  const [loadingReportText, setLoadingReportText] = useState<string | undefined>(undefined);

  const createPropertyReport = (
    entityId: string,
    propertyReportType: ReportType,
    options?: ReportOptions,
  ): Promise<ReportResult> => {
    const { date, ...otherOptions } = options || {};

    return new Promise((resolve, reject) => {
      let hasReturned = false;
      setLoadingReportText("Generating Report...");
      ReportService.getPropertyReport(entityId, propertyReportType, {
        date: date || dayjs().subtract(1, "month").format(DATE_FORMATS.MONTH_SHORT_YEAR_SPACED),
        ...otherOptions,
      })
        .then((report: ReportResult) => {
          hasReturned = true;
          setLoadingReportText(undefined);
          resolve(report);
        })
        .catch((err) => {
          hasReturned = true;
          setLoadingReportText(undefined);
          reject(err);
        });
      setTimeout(() => {
        if (!hasReturned) {
          setLoadingReportText("Still Generating...");
        }
      }, 6000);
    });
  };

  return { loadingReportText, createPropertyReport };
}

export function useCustomPropertyReportMutation() {
  const [loadingReportText, setLoadingReportText] = useState<string | undefined>(undefined);

  const createReport = async ({
    entityId,
    reportType,
    options,
  }: {
    entityId: string;
    reportType: ReportType;
    options?: Record<string, string>;
  }): Promise<ReportResult> => {
    let hasReturned = false;
    setLoadingReportText("Generating Report...");

    const result = await api.post("/v2/report", {
      entityName: ReportEntityNames[reportType],
      entityId,
      type: reportType,
      parameters: options,
    });

    setTimeout(() => {
      if (!hasReturned) {
        setLoadingReportText("Still Generating...");
      }
    }, 6000);

    try {
      const reportResult = (await ReportService.pollReportResult(result.id)) as ReportResult;
      hasReturned = true;
      setLoadingReportText(undefined);
      return reportResult;
    } catch (err) {
      hasReturned = true;
      setLoadingReportText(undefined);
      throw err;
    }
  };

  const mutation = useMutation(createReport);

  return { ...mutation, loadingReportText };
}

export const useGetPropertyPalaceId = (propertyId: string) => {
  return useQuery<string>(
    [QueryKeys.PALACE_ID, { propertyId }],
    () => api.get(`/v2/installation/${propertyId}/palacepropertyid`),
    {
      enabled: Boolean(propertyId),
      retry: false, // 404 is a valid response
      staleTime: Infinity,
    },
  );
};

export type PropertyPalaceIdMap = {
  propertyId: string;
  palaceId: string;
};

export const useGetPalaceIdMap = (propertyIds: string[]) => {
  // Palace ids for the properties the caller named, not for the whole organisation. It
  // took propertyIds all along and then ignored them, loading all 1,500 rows to build a
  // map the caller only ever reads a handful of keys from.
  const { properties } = useGetPropertyInfoByIds(propertyIds);

  const palaceIdMap: Record<string, string> = {};
  for (const property of properties) {
    if (property.palaceId) {
      palaceIdMap[property.id] = property.palaceId;
    }
  }

  return {
    palaceIdMap,
  };
};

export interface RemoveDeviceInput {
  locationId: string;
  locationType: LocationType;
}

export const useRemoveDeviceMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { errorToast, successToast } = useTetherToast();

  const handleRemoveDevice = ({ input }: { input: RemoveDeviceInput }) => {
    const { locationId, locationType } = input;

    switch (locationType) {
      case LocationType.RoomLocation:
        return api.del(`property/v2/roomlocation/${locationId}/device`);
      case LocationType.Outside:
        return api.del(`property/v2/outside/${locationId}/device`);
      case LocationType.Circuit:
        return api.del(`property/v2/circuit/${locationId}/device`);
      case LocationType.CircuitNode:
        return api.del(`property/v2/circuitnode/${locationId}/device`);
      default:
        throw new Error(`Unknown Location Type: ${locationType}`);
    }
  };

  return useMutation(handleRemoveDevice, {
    onError: (e: Error) => {
      errorToast({
        title: "Unable to remove device",
        description: e.message,
      });
    },
    onSuccess: (data, { input }) => {
      queryClient.invalidateQueries(QueryKeys.ROOMS);
      queryClient.invalidateQueries(QueryKeys.PROPERTY);
      queryClient.invalidateQueries(QueryKeys.CIRCUITNODES);
      if (onSuccess) {
        onSuccess();
      }
      successToast({
        title: "Successfully removed device",
      });
    },
  });
};

interface TagReponse {
  tags: string[];
}

export const useGetPropertyTags = (id: string, options?: UseQueryOptions<TagReponse, Error>) => {
  const { data, ...rest } = useQuery<TagReponse, Error>(
    [QueryKeys.TAGS, id],
    () => api.get(`property/v2/property/${id}/tag`),
    {
      enabled: Boolean(id),
      staleTime: Infinity,
      ...options,
    },
  );
  return { tags: data?.tags, ...rest };
};

/**
 * Tags for a list of properties, ONE REQUEST PER PROPERTY.
 *
 * Has no callers, and should not gain one: `organisation-property-info` already returns
 * `propertyTags` on every row, so anything holding that list already has the tags. The
 * management report used this and was issuing 1,500 requests to re-fetch data it had.
 *
 * Kept only for a caller that holds bare property ids and nothing else. If you are about
 * to use it, check whether the list you have came from a property-info endpoint first.
 */
export const useGetPropertyTagsForMultipleProperties = (
  ids: string[],
  options?: UseQueryOptions<TagReponse, Error>,
) => {
  return useAggregatedQueries(
    !ids.length
      ? []
      : ids.map((id) => {
          const curr = id;
          return {
            queryKey: [QueryKeys.TAGS, curr],
            queryFn: () => api.get(`property/v2/property/${id}/tag`),
            select: (res: any) => ({ id: curr, tags: res?.tags }),
            staleTime: 60000,
          };
        }),
  );
};

export const useGetAllPropertyTags = (options?: UseQueryOptions<string[], Error>) => {
  const { data: tags, ...rest } = useQuery<string[], Error>(
    [QueryKeys.TAGS],
    () => api.get(`property/v2/propertytag`),
    {
      staleTime: Infinity,
      ...options,
    },
  );
  return { tags, ...rest };
};

export const useAddPropertyTagMutation = (onSuccess?: (tag: string) => void) => {
  const queryClient = useQueryClient();

  const updateTags = async ({ propertyId, tag }: { propertyId: string; tag: string }) => {
    try {
      const res = await api.put(`property/v2/property/${propertyId}/tag`, { tag });
      return { propertyId, ...res };
    } catch (e) {
      console.log("Unable to add tag");
    }
  };

  return useMutation(updateTags, {
    onSuccess: (data) => {
      queryClient.invalidateQueries([QueryKeys.PROPERTY, data.propertyId]);
      queryClient.invalidateQueries(QueryKeys.TAGS);
      if (onSuccess) {
        onSuccess(data.tags);
      }
    },
  });
};

export const useRemovePropertyTagMutation = (onSuccess?: (tag: string) => void) => {
  const queryClient = useQueryClient();

  const updateTags = async ({ propertyId, tag }: { propertyId: string; tag: string }) => {
    try {
      const res = await api.del(`property/v2/property/${propertyId}/tag`, { tag });
      return { propertyId, ...res };
    } catch (e) {
      console.log("Unable to remove tag");
    }
  };

  return useMutation(updateTags, {
    onSuccess: (data) => {
      queryClient.invalidateQueries([QueryKeys.PROPERTY, data.propertyId]);
      queryClient.invalidateQueries(QueryKeys.TAGS);
      if (onSuccess) {
        onSuccess(data.tags);
      }
    },
  });
};

export const useGetOrganisationTags = () => {
  const { user } = useAuth();
  const { data, ...rest } = useQuery<OrganisationTag[], Error>(
    [QueryKeys.ORGANISATION_TAGS, user?.currentOrganisationId],
    () => api.get(`v2/organisationTag?organisationId=${user!.currentOrganisationId}`),
    {
      enabled: Boolean(user?.currentOrganisationId),
      staleTime: Infinity,
    },
  );
  return { organisationTags: data || [], ...rest };
};

// Create OrganisationTag
export const useCreateOrganisationTag = () => {
  const queryClient = useQueryClient();

  const handleCreateOrganisationTag = async ({
    input,
  }: { input: Omit<OrganisationTag, "id"> } & MutationProps) => {
    return await api.post("/v2/organisationTag", { ...input });
  };

  return useMutation(handleCreateOrganisationTag, {
    onError: (error: unknown, { onError }) => {
      if (onError) onError(error);
    },
    onSuccess: (data, { input, onSuccess }) => {
      queryClient.invalidateQueries([QueryKeys.ORGANISATION_TAGS, input.organisationId]);

      if (onSuccess) onSuccess(data);
    },
  });
};

// Update OrganisationTag
export const useUpdateOrganisationTag = () => {
  const queryClient = useQueryClient();

  const handleUpdateOrganisationTag = async ({ input }: { input: OrganisationTag } & MutationProps) => {
    return await api.put(`/v2/organisationTag/${input.id}`, { ...input });
  };

  return useMutation(handleUpdateOrganisationTag, {
    onError: (error: unknown, { onError }) => {
      if (onError) onError(error);
    },
    onSuccess: (data, { input, onSuccess }) => {
      queryClient.invalidateQueries([QueryKeys.ORGANISATION_TAGS, input.organisationId]);

      if (onSuccess) onSuccess(data);
    },
  });
};

// Delete OrganisationTag
export const useDeleteOrganisationTag = () => {
  const queryClient = useQueryClient();

  const handleDeleteOrganisationTag = async ({
    input,
  }: { input: { id: string; organisationId: string } } & MutationProps) => {
    return await api.del(`/v2/organisationTag/${input.id}`);
  };

  return useMutation(handleDeleteOrganisationTag, {
    onError: (error: unknown, { onError }) => {
      if (onError) onError(error);
    },
    onSuccess: (data, { input, onSuccess }) => {
      queryClient.invalidateQueries([QueryKeys.ORGANISATION_TAGS, input.organisationId]);

      if (onSuccess) onSuccess(data);
    },
  });
};

export const useGetCircuitNodeTree = (propertyId: string | undefined) => {
  const { data, ...rest } = useAggregatesV2Query<CircuitNode[], Error>(
    [QueryKeys.CIRCUITNODES, propertyId],
    (_, useV2Aggregates) =>
      api.get(`/experience/v1/circuittree/${propertyId}`, {
        usage: false,
        ...(useV2Aggregates && { useV2: true })
      }),
    {
      enabled: Boolean(propertyId),
      staleTime: Infinity,
    },
  );
  return { circuitNodeTree: data, ...rest };
};

export const useGetCircuitNodeTreeWithUsage = (propertyId: string | undefined, dateRange: TimestampRange) => {
  const { data, ...rest } = useAggregatesV2Query<CircuitNodeWithUsage[], Error>(
    [QueryKeys.CIRCUITNODES, propertyId, "USAGE", dateRange.fromTimestamp, dateRange.toTimestamp],
    (_, useV2Aggregates) =>
      api.get(
        `/experience/v1/circuittree/${propertyId}`,
        {
          usage: true,
          fromTimestamp: dateRange.fromTimestamp,
          toTimestamp: dateRange.toTimestamp,
          ...(useV2Aggregates && { useV2: true })
        }
      ),
    {
      enabled: Boolean(propertyId && dateRange.fromTimestamp && dateRange.toTimestamp),
      staleTime: Infinity,
    },
  );
  return { circuitNodeTree: data, ...rest };
};

/** kWh attributed to one organisation tag over a window. */
export interface TagEnergyTotal {
  tagId: string;
  totalKwh: number;
}

export interface OrganisationEnergyByTag {
  /** Organisation-wide totals, one entry per tag with any energy in the window. */
  tags: TagEnergyTotal[];
  /** The same totals broken down by property, for the per-property energy column. */
  properties: { propertyId: string; tags: TagEnergyTotal[] }[];
  /**
   * Circuit nodes whose readings could not be resolved.
   *
   * A partial total that looks complete is the failure mode this replaces — the pie has no
   * error state — so the number is reported rather than swallowed.
   */
  unreadableNodeCount: number;
}

/**
 * Energy per organisation tag, for the whole organisation, in ONE request.
 *
 * What it replaces: one `/experience/v1/circuittree` call PER PROPERTY, each resolving a
 * reading per circuit node, fired the moment somebody opened the portfolio list view — and
 * then the tag totals summed in the browser. On a 1,500-property organisation that is
 * 1,500 requests and it takes the API down. Only tagged nodes ever contributed, and
 * tagging is manual curation, so the endpoint reads those directly.
 */
export const useGetOrganisationEnergyByTag = (
  dateRange: TimestampRange,
  options?: { enabled?: boolean },
) => {
  const { user } = useAuth();
  const organisationId = user?.currentOrganisationId;

  const { data, ...rest } = useAggregatesV2Query<OrganisationEnergyByTag, Error>(
    [QueryKeys.ORGANISATION_ENERGY_BY_TAG, organisationId, dateRange.fromTimestamp, dateRange.toTimestamp],
    (_, useV2Aggregates) =>
      api.get(`/experience/v1/organisation/${organisationId}/energy-by-tag`, {
        fromTimestamp: dateRange.fromTimestamp,
        toTimestamp: dateRange.toTimestamp,
        ...(useV2Aggregates && { useV2: true }),
      }),
    {
      enabled:
        (options?.enabled ?? true) &&
        Boolean(organisationId && dateRange.fromTimestamp && dateRange.toTimestamp),
      staleTime: Infinity,
    },
  );

  return { energyByTag: data, ...rest };
};

interface UseGetCircuitNodePowerOptions extends TimestampRange {
  resolution?: ReadingResolution;
  tagId?: string;
  interpolateMissing?: boolean;
}

export const useGetCircuitNodePower = (
  circuitNodeId: string | undefined,
  query: UseGetCircuitNodePowerOptions,
  options: UseQueryOptions<POWER_READING, Error> = {},
) => {

  const { data, ...rest } = useAggregatesV2Query<POWER_READING, Error>(
    [QueryKeys.CIRCUITNODES, circuitNodeId, "POWER", query],
    (_, useV2Aggregates) =>
      api.get(`/property/v2/circuitNode/${circuitNodeId}/power`, {
        resolution: query.resolution ?? "TOTAL",
        fromTimestamp: query.fromTimestamp,
        toTimestamp: query.toTimestamp,
        ...(useV2Aggregates && { useV2: true }),
        ...(query.interpolateMissing && { interpolateMissing: true }),
        ...(query.tagId && { tagId: query.tagId }),
      }),
    {
      enabled: Boolean(circuitNodeId && query.fromTimestamp && query.toTimestamp),
      staleTime: Infinity,
      ...options,
    },
  );

  return { circuitNodePower: data, ...rest };
};

interface CombinedCircuitNodePowerProps {
  circuitNodeIds: string[];
  tagId?: string;
  dateRange: TimestampRange;
  resolution?: ReadingResolution;
  enabled?: boolean;
  interpolateMissing?: boolean;
}

export function useGetCombinedCircuitNodePower(
  x: CombinedCircuitNodePowerProps & { resolution: Exclude<ReadingResolution, "TOTAL"> },
): AggregatedQueriesResponse<POWER_READING[] | undefined, Error>;
export function useGetCombinedCircuitNodePower(
  x: CombinedCircuitNodePowerProps & { resolution: "TOTAL" },
): AggregatedQueriesResponse<POWER_READING | undefined, Error>;
export function useGetCombinedCircuitNodePower({
  circuitNodeIds,
  tagId,
  dateRange: { fromTimestamp, toTimestamp },
  resolution = "TOTAL",
  enabled = true,
  interpolateMissing
}: CombinedCircuitNodePowerProps): AggregatedQueriesResponse<
  POWER_READING | POWER_READING[] | undefined,
  Error
> {

  return useAggregatesV2Queries<POWER_READING | POWER_READING[] | undefined, Error>(
    circuitNodeIds.map((circuitNodeId) => ({
      queryKey: [
        QueryKeys.CIRCUITNODES,
        circuitNodeId,
        tagId,
        "POWER",
        fromTimestamp,
        toTimestamp,
        resolution,
        interpolateMissing
      ],
      queryFn: async (_, useV2Aggregates) => {
        let result = await api.get(`/property/v2/circuitNode/${circuitNodeId}/power`, {
          resolution,
          fromTimestamp,
          toTimestamp,
          ...(useV2Aggregates && { useV2: true }),
          ...(interpolateMissing && { interpolateMissing }),
          ...(tagId && { tagId }),
        });
        if (resolution === "TOTAL") {
          if (!("startTimestamp" in result)) result = undefined;
          return result as POWER_READING;
        }

        return result as POWER_READING[];
      },
      enabled: enabled && Boolean(circuitNodeId && fromTimestamp && toTimestamp),
      staleTime: Infinity,
      retry: false,
    })),
  );
};

export const useGetCircuitNodeAndSubnodes = (propertyId: string | undefined, circuitNodeId: string) => {
  const { circuitNodeTree, refetch, isLoading, isRefetching, error } = useGetCircuitNodeTree(propertyId);

  const getCircuitNodeFromTree = useCallback((tree: CircuitNode[] | undefined, circuitNodeId: string): CircuitNode | undefined => {
    if (!circuitNodeId) return undefined;
    if (!tree) return undefined;
    const circuitNode = tree?.find((circuitNode) => circuitNode.id === circuitNodeId);
    if (circuitNode) {
      return circuitNode;
    }
    for (const circuitNode of tree) {
      const childCircuitNode = getCircuitNodeFromTree(circuitNode.children, circuitNodeId);
      if (childCircuitNode) {
        return childCircuitNode;
      }
    }
    return undefined;
  }, []);

  const targetNode = useMemo(
    () => getCircuitNodeFromTree(circuitNodeTree, circuitNodeId),
    [getCircuitNodeFromTree, circuitNodeTree, circuitNodeId],
  );

  return {
    circuitNode: targetNode,
    isLoading,
    isRefetching,
    refetch,
    error
  };
};

export interface CreateCircuitNodeInput {
  id?: string;
  propertyId: string;
  name: string;
  type: CircuitNodeType;
  parentId: string | null;
  tagIds: string[];
}

export const useCreateCircuitNode = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  const handleCreateCircuitNode = async ({ input }: { input: CreateCircuitNodeInput }) => {
    return api.post(`property/v2/circuitNode`, input);
  };

  return useMutation(handleCreateCircuitNode, {
    onSuccess: (data, { input }) => {
      queryClient.invalidateQueries([QueryKeys.CIRCUITNODES, input.propertyId]);
      queryClient.invalidateQueries([QueryKeys.PROPERTY, input.propertyId]);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export const useEditCircuitNode = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  const handleEditCircuitNode = async ({ input }: { input: CreateCircuitNodeInput }) => {
    return api.put(`property/v2/circuitNode/${input.id}`, input);
  };

  return useMutation(handleEditCircuitNode, {
    onSuccess: (data, { input }) => {
      queryClient.invalidateQueries([QueryKeys.CIRCUITNODES, input.propertyId]);
      queryClient.invalidateQueries([QueryKeys.PROPERTY, input.propertyId]);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export const useDeleteCircuitNode = () => {
  const queryClient = useQueryClient();

  const handleDeleteCircuitNode = async ({
    input,
  }: { input: { circuitNode: CircuitNode; propertyId: string } } & MutationProps) => {
    if (input.circuitNode.circuits.filter((c) => c.device).length > 0) {
      throw new Error("You can't delete a circuit with devices attached");
    }

    for (let i = 0; i < input.circuitNode.circuits.length; i++) {
      const circuit = input.circuitNode.circuits[i];
      await api.del(`/property/v2/circuit/${circuit.id}`);
    }

    return await api.del(`/property/v2/circuitNode/${input.circuitNode.id}`);
  };

  return useMutation(handleDeleteCircuitNode, {
    onError: (error: unknown, { onError }) => {
      if (onError) onError(error);
    },
    onSuccess: (data, { input, onSuccess }) => {
      queryClient.invalidateQueries([QueryKeys.CIRCUITNODES, input.propertyId]);
      queryClient.invalidateQueries([QueryKeys.PROPERTY, input.propertyId]);

      if (onSuccess) onSuccess(data);
    },
  });
};

export const useUpdateOrganisationMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    ({ input }: { input: Organisation }) => api.put(`/customer/v1/organisation/${input.id}`, input),
    {
      onError: () => {
        toast({
          title: "Unable to update organisation",
          status: "error",
          isClosable: true,
          position: "top-right",
        });
      },
      onSuccess: (data, { input }) => {
        toast({
          title: "Organisation Updated",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        // Invalidate and refetch
        queryClient.invalidateQueries(QueryKeys.ORGANISATION);
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );
};

export const useCreateOrganisationMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    ({ input }: { input: Partial<Organisation> }) => api.post(`/customer/v1/organisation`, input),
    {
      onError: (error: Error) => {
        console.log(error);
        toast({
          title: "Unable to create organisation: " + error.message,
          status: "error",
          isClosable: true,
          position: "top-right",
        });
      },
      onSuccess: (data, { input }) => {
        toast({
          title: "Organisation Created",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        // Invalidate and refetch
        queryClient.invalidateQueries(QueryKeys.ORGANISATION);
        queryClient.invalidateQueries(UserQueryKeys.USER_ORGANISATION);
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );
};

export const useGetCustomerOrgs = (customerId: string | undefined) => {
  const { data, ...rest } = useQuery<Organisation[], Error>(
    [QueryKeys.ORGANISATION],
    () => api.get(`/customer/v1/organisation?customerId=${customerId}`),
    {
      enabled: Boolean(customerId),
      staleTime: Infinity,
    },
  );
  return { organisations: data, ...rest };
};

export interface Customer {
  id: string;
  name: string;
}

export const useGetCurrentCustomer = () => {
  const { data, ...rest } = useQuery<Customer, Error>(
    [QueryKeys.CUSTOMER, "current"],
    () => api.get(`/account/v2/customer/current`),
    {
      staleTime: Infinity,
    },
  );
  return { customer: data, ...rest };
};

export const useUpdateCustomerMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    ({ input }: { input: Customer }) => api.put(`/customer/v1/customer/${input.id}`, input),
    {
      onError: () => {
        toast({
          title: "Unable to update customer",
          status: "error",
          isClosable: true,
          position: "top-right",
        });
      },
      onSuccess: (data, { input }) => {
        toast({
          title: "Customer Updated",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        // Invalidate and refetch
        queryClient.invalidateQueries(QueryKeys.CUSTOMER);
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );
};

export const useGetUserCustomers = () => {
  const { data: userCustomers, ...rest } = useQuery<Customer[]>(
    [QueryKeys.CUSTOMER, "all"],
    () => api.get(`/customer/v1/customer`),
    {
      staleTime: Infinity,
    },
  );

  return { userCustomers, ...rest };
};

